import NumberInput from "../../../components/NumberInput";
import React, { useMemo, useState } from "react";
import { FieldFeedback, RequiredMark } from "../../../components/FormValidation";
import useFormValidation from "../../../hooks/useFormValidation";
import { validationRules } from "../../../utils/helpers/formValidation";
import { formatIDR } from "../../../utils/helpers/formatIDR";
import ModalShell from "../../CashBankPage/components/ModalShell";
import AkasSelect from "../../../components/AkasSelect";
import { PERIOD_OPTIONS, formatDateTime, localDateTime } from "../constants";

const randomKey = () => window.crypto?.randomUUID?.() || `${Date.now()}-${Math.random().toString(36).slice(2)}`;

const FormModal = ({ expense, categories = [], accounts = [], saving, onClose, onSave }) => {
  const isEdit = Boolean(expense?.id);
  const [form, setForm] = useState({
    category_id: expense?.category_id || categories[0]?.id || "",
    financial_account_id: expense?.financial_account_id || accounts[0]?.id || "",
    amount: isEdit ? String(expense?.amount || "") : "",
    period_type: expense?.period_type || "monthly",
    occurred_at: expense?.occurred_at ? localDateTime(expense.occurred_at) : localDateTime(),
    is_recurring: Boolean(expense?.schedule_is_active),
    recurrence_ends_at: expense?.schedule_ends_at ? localDateTime(expense.schedule_ends_at) : "",
    note: expense?.note || "",
  });
  const selectedAccount = useMemo(
    () => accounts.find((account) => Number(account.id) === Number(form.financial_account_id)),
    [accounts, form.financial_account_id],
  );
  const schema = useMemo(() => ({
    category_id: [validationRules.required("Kategori biaya wajib dipilih.")],
    period_type: [validationRules.required("Periode biaya wajib dipilih."), validationRules.oneOf(PERIOD_OPTIONS.map((item) => item.value))],
    note: [validationRules.maxLength(1000, "Keterangan maksimal 1.000 karakter.")],
    ...(form.is_recurring ? {
      recurrence_ends_at: [validationRules.custom((value) => value && new Date(value) <= new Date(form.occurred_at)
        ? "Tanggal berakhir harus setelah waktu transaksi pertama."
        : "")],
    } : {}),
    ...(!isEdit ? {
      financial_account_id: [validationRules.required("Akun Kas & Bank wajib dipilih.")],
      amount: [
        validationRules.required("Jumlah biaya wajib diisi."),
        validationRules.number(),
        validationRules.integer("Jumlah biaya harus berupa rupiah bulat."),
        validationRules.min(1, "Jumlah biaya minimal Rp1."),
        validationRules.custom((value) => Number(value || 0) > Number(selectedAccount?.current_balance || 0)
          ? "Jumlah biaya melebihi saldo akun yang tersedia."
          : ""),
      ],
      occurred_at: [validationRules.required("Waktu transaksi wajib diisi.")],
    } : {}),
  }), [form.is_recurring, form.occurred_at, isEdit, selectedAccount?.current_balance]);
  const validation = useFormValidation({ values: form, schema, formId: "operational-expense-form" });

  const update = (key, value) => setForm((current) => ({ ...current, [key]: value }));
  const save = (event) => {
    event.preventDefault();
    if (!validation.validateAll()) return;

    if (isEdit) {
      onSave({
        category_id: Number(form.category_id),
        period_type: form.period_type,
        is_recurring: form.period_type !== "once" && form.is_recurring,
        recurrence_ends_at: form.is_recurring && form.recurrence_ends_at ? form.recurrence_ends_at : null,
        note: form.note.trim() || null,
      });
      return;
    }

    onSave({
      category_id: Number(form.category_id),
      financial_account_id: Number(form.financial_account_id),
      amount: Number(form.amount),
      period_type: form.period_type,
      occurred_at: form.occurred_at,
      is_recurring: form.period_type !== "once" && form.is_recurring,
      recurrence_ends_at: form.is_recurring && form.recurrence_ends_at ? form.recurrence_ends_at : null,
      note: form.note.trim() || null,
      idempotency_key: randomKey(),
    });
  };

  return (
    <ModalShell
      eyebrow={isEdit ? "EDIT INFORMASI" : "PENCATATAN BIAYA"}
      title={isEdit ? "Edit biaya operasional" : "Tambah biaya operasional"}
      subtitle={isEdit
        ? "Informasi kategori, periode, dan keterangan dapat diperbarui tanpa mengubah audit transaksi."
        : "Saldo akun terpilih akan langsung berkurang dan biaya masuk perhitungan laba bersih operasional."}
      onClose={onClose}
      onSubmit={save}
      saving={saving}
      submitText={isEdit ? "Simpan perubahan" : "Simpan biaya"}>
      {isEdit ? (
        <div className="operational-expense-locked-grid" aria-label="Informasi transaksi yang dikunci">
          <div><span>Nominal</span><strong>{formatIDR(expense.amount)}</strong></div>
          <div><span>Akun pembayaran</span><strong>{expense.account_name || "-"}</strong></div>
          <div><span>Waktu transaksi</span><strong>{formatDateTime(expense.occurred_at)}</strong></div>
          <p>Nominal, akun, dan waktu dikunci untuk menjaga audit. Jika salah, batalkan transaksi lalu catat ulang.</p>
        </div>
      ) : null}

      <div className="row g-3">
        <div className={isEdit ? "col-md-7" : "col-md-6"}>
          <label className="form-label">Kategori biaya <RequiredMark /></label>
          <AkasSelect
            className={validation.inputClass("category_id", "form-select")}
            {...validation.fieldProps("category_id")}
            value={form.category_id}
            onChange={(event) => update("category_id", event.target.value)}>
            <option value="">Pilih kategori</option>
            {categories.map((category) => <option key={category.id} value={category.id}>{category.name}</option>)}
          </AkasSelect>
          <FieldFeedback formId="operational-expense-form" name="category_id" error={validation.errors.category_id} />
        </div>

        {!isEdit ? (
          <div className="col-md-6">
            <label className="form-label">Dibayar dari <RequiredMark /></label>
            <AkasSelect
              className={validation.inputClass("financial_account_id", "form-select")}
              {...validation.fieldProps("financial_account_id")}
              value={form.financial_account_id}
              onChange={(event) => update("financial_account_id", event.target.value)}>
              <option value="">Pilih akun Kas & Bank</option>
              {accounts.map((account) => (
                <option key={account.id} value={account.id}>{account.name} · {formatIDR(account.current_balance)}</option>
              ))}
            </AkasSelect>
            <FieldFeedback formId="operational-expense-form" name="financial_account_id" error={validation.errors.financial_account_id} />
          </div>
        ) : null}

        {!isEdit ? (
          <>
            <div className="col-md-6">
              <label className="form-label">Jumlah biaya <RequiredMark /></label>
              <div className="input-group">
                <span className="input-group-text">Rp</span>
                <NumberInput
                  autoFocus
                  type="number"
                  min="1"
                  step="1"
                  inputMode="numeric"
                  className={validation.inputClass("amount")}
                  {...validation.fieldProps("amount")}
                  value={form.amount}
                  onChange={(event) => update("amount", event.target.value)}
                  placeholder="0"
                />
              </div>
              <FieldFeedback formId="operational-expense-form" name="amount" error={validation.errors.amount} />
            </div>
            <div className="col-md-6">
              <label className="form-label">Waktu transaksi <RequiredMark /></label>
              <input
                type="datetime-local"
                className={validation.inputClass("occurred_at")}
                {...validation.fieldProps("occurred_at")}
                value={form.occurred_at}
                onChange={(event) => update("occurred_at", event.target.value)}
              />
              <FieldFeedback formId="operational-expense-form" name="occurred_at" error={validation.errors.occurred_at} />
            </div>
          </>
        ) : null}

        <div className="col-12">
          <label className="form-label">Periode biaya <RequiredMark /></label>
          <div className="operational-expense-period-grid" data-validation-field="period_type">
            {PERIOD_OPTIONS.map((item) => (
              <label key={item.value} className={form.period_type === item.value ? "is-active" : ""}>
                <input
                  type="radio"
                  name="period_type"
                  value={item.value}
                  checked={form.period_type === item.value}
                  onChange={(event) => setForm((current) => ({
                    ...current,
                    period_type: event.target.value,
                    is_recurring: event.target.value === "once" ? false : current.is_recurring,
                  }))}
                />
                <span>{item.label}</span>
                <small>{item.description}</small>
              </label>
            ))}
          </div>
          <FieldFeedback formId="operational-expense-form" name="period_type" error={validation.errors.period_type} />
        </div>

        <div className="col-12">
          <section className={`operational-expense-recurring-card ${form.is_recurring ? "is-active" : ""}`}>
            <div className="operational-expense-recurring-copy">
              <span className="operational-expense-recurring-icon" aria-hidden="true">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
                  <path d="M20 7h-9a4 4 0 0 0-4 4v1M4 17h9a4 4 0 0 0 4-4v-1" />
                  <path d="m17 4 3 3-3 3M7 14l-3 3 3 3" />
                </svg>
              </span>
              <div>
                <strong>Jadwalkan otomatis</strong>
                <small>
                  {form.period_type === "once"
                    ? "Pilih periode berulang untuk mengaktifkan scheduler."
                    : `Biaya akan dicatat otomatis setiap ${PERIOD_OPTIONS.find((item) => item.value === form.period_type)?.label.toLowerCase() || "periode"}.`}
                </small>
              </div>
            </div>
            <label className="form-check form-switch m-0" aria-label="Aktifkan jadwal otomatis">
              <input
                type="checkbox"
                className="form-check-input"
                checked={form.is_recurring}
                disabled={form.period_type === "once"}
                onChange={(event) => update("is_recurring", event.target.checked)}
              />
            </label>
          </section>

          {form.is_recurring ? (
            <div className="operational-expense-recurring-settings animate-fade-up">
              <div>
                <label className="form-label" htmlFor="operational-expense-recurrence-ends-at">Berakhir pada</label>
                <input
                  id="operational-expense-recurrence-ends-at"
                  type="datetime-local"
                  className={validation.inputClass("recurrence_ends_at")}
                  {...validation.fieldProps("recurrence_ends_at")}
                  min={form.occurred_at}
                  value={form.recurrence_ends_at}
                  onChange={(event) => update("recurrence_ends_at", event.target.value)}
                />
                <FieldFeedback
                  formId="operational-expense-form"
                  name="recurrence_ends_at"
                  error={validation.errors.recurrence_ends_at}
                  hint="Opsional. Kosongkan jika jadwal tidak memiliki tanggal akhir."
                />
              </div>
              <p>Setiap pencatatan otomatis menjadi transaksi ledger baru dan tidak mengubah histori sebelumnya.</p>
            </div>
          ) : null}
        </div>

        <div className="col-12">
          <label className="form-label">Keterangan</label>
          <textarea
            rows="3"
            maxLength="1000"
            className={validation.inputClass("note")}
            {...validation.fieldProps("note")}
            value={form.note}
            onChange={(event) => update("note", event.target.value)}
            placeholder="Contoh: Tagihan listrik outlet bulan Agustus..."
          />
          <FieldFeedback formId="operational-expense-form" name="note" error={validation.errors.note} />
        </div>

        {!isEdit && selectedAccount ? (
          <div className="operational-expense-balance-info">
            <div><span>Saldo tersedia</span><strong>{formatIDR(selectedAccount.current_balance)}</strong></div>
            <div><span>Sisa setelah dicatat</span><strong>{formatIDR(Number(selectedAccount.current_balance || 0) - Number(form.amount || 0))}</strong></div>
          </div>
        ) : null}
      </div>
    </ModalShell>
  );
};

export default FormModal;
